import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Alert, FormGroup, Form, Modal, ModalBody, ModalHeader } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Dropzone from 'react-dropzone';
import api, { handleApiError } from '../../../helpers/api';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

// Custom CSS for the loading modal
const customStyles = `
  .loading-modal .modal-content {
    background: transparent;
    border: none;
    box-shadow: none;
  }
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 4px solid #0ab39c;
    border-top: 4px solid transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ResourceForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { resourceId } = useParams();
    const isEdit = !!resourceId;

    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState([]);
    const [selectedFile, setSelectedFile] = useState([]);
    const [deleteOldImage, setDeleteOldImage] = useState(false);
    const [deleteOldFile, setDeleteOldFile] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // State for loading modal

    const validation = useFormik({
        initialValues: {
            titre: '',
            description: '',
            type: '',
            categorie: '',
            tags: [],
            fichierOuLien: null,
            fichierOuLienType: 'file',
            openGraphImage: null,
            seoTitle: '',
            metaDescription: '',
            urlPropre: '',
        },
        validationSchema: Yup.object({
            titre: Yup.string().required(t('create_resource.validation.titre_required')),
            description: Yup.string().required(t('create_resource.validation.description_required')),
            type: Yup.string().required(t('create_resource.validation.type_required')),
            categorie: Yup.string().required(t('create_resource.validation.categorie_required')),
            tags: Yup.array().min(1, t('create_resource.validation.tags_min')),
            fichierOuLien: Yup.mixed()
                .required(t('create_resource.validation.fichierOuLien_required'))
                .test('fileOrUrl', t('create_resource.validation.fichierOuLien_invalid'), function (value) {
                    const { fichierOuLienType } = this.parent;
                    if (fichierOuLienType === 'file' && !value) return false;
                    if (fichierOuLienType === 'url' && value) {
                        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
                        return youtubeRegex.test(value);
                    }
                    return true;
                }),
            openGraphImage: Yup.mixed().required(t('create_resource.validation.openGraphImage_required')),
            seoTitle: Yup.string().required(t('create_resource.validation.seoTitle_required')),
            metaDescription: Yup.string()
                .required(t('create_resource.validation.metaDescription_required'))
                .max(160, t('create_resource.validation.metaDescription_max')),
            urlPropre: Yup.string()
                .required(t('create_resource.validation.urlPropre_required'))
                .matches(/^\/[a-z0-9-/]+$/i, t('create_resource.validation.urlPropre_invalid')),
        }),
        onSubmit: async (values) => {
            setIsLoading(true); // Show loading modal
            try {
                const data = new FormData();
                Object.keys(values).forEach((key) => {
                    if (key === 'tags') {
                        data.append(key, JSON.stringify(values[key]));
                    } else if (key === 'openGraphImage' && values[key]) {
                        data.append(key, values[key]);
                    } else if (key === 'fichierOuLien' && values[key] && values.fichierOuLienType === 'file') {
                        data.append(key, values[key]);
                    } else if (key !== 'fichierOuLienType') {
                        data.append(key, values[key]);
                    }
                });

                if (isEdit) {
                    if (selectedImage[0] && selectedImage[0].preview !== initialImageUrl) {
                        data.append('deleteOldImage', deleteOldImage);
                    }
                    if (selectedFile[0] && selectedFile[0].preview !== initialFileUrl && values.fichierOuLienType === 'file') {
                        data.append('deleteOldFile', deleteOldFile);
                    }
                    await api.patch(`/ressource/${resourceId}`, data, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        withCredentials: true,
                    });
                    toast.success(t('create_resource.toasts.update_success'));
                } else {
                    await api.post('/ressource', data, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        withCredentials: true,
                    });
                    toast.success(t('create_resource.toasts.create_success'));
                }
                navigate('/apps-resource-list');
            } catch (err) {
                if (err.response?.status === 401 || err.response?.status === 403) {
                    await handleApiError(err, dispatch, navigate);
                } else {
                    setError(t(isEdit ? 'create_resource.error.update_failed' : 'create_resource.error.create_failed'));
                    toast.error(t(isEdit ? 'create_resource.toasts.update_failed' : 'create_resource.toasts.create_failed'));
                }
            } finally {
                setIsLoading(false); // Hide loading modal
            }
        },
    });

    const [initialImageUrl, setInitialImageUrl] = useState(null);
    const [initialFileUrl, setInitialFileUrl] = useState(null);

    useEffect(() => {
        if (isEdit) {
            const fetchResource = async () => {
                try {
                    console.log(`Fetching resource with ID: ${resourceId}`);
                    const response = await api.get(`/ressource/${resourceId}`, {
                        withCredentials: true,
                    });

                    const resource = response.data.data;
                    validation.setValues({
                        titre: resource.titre || '',
                        description: resource.description || '',
                        type: resource.type || '',
                        categorie: resource.categorie || '',
                        tags: resource.tags || [],
                        fichierOuLien: resource.fichierOuLien || null,
                        fichierOuLienType: resource.fichierOuLienType || 'file',
                        openGraphImage: resource.openGraphImage || null,
                        seoTitle: resource.seoTitle || '',
                        metaDescription: resource.metaDescription || '',
                        urlPropre: resource.urlPropre || '',
                    });
                    if (resource.openGraphImage) {
                        setSelectedImage([{ preview: resource.openGraphImage, name: 'openGraphImage', formattedSize: '' }]);
                        setInitialImageUrl(resource.openGraphImage);
                    }
                    if (resource.fichierOuLien && resource.fichierOuLienType === 'file') {
                        setSelectedFile([{ preview: resource.fichierOuLien, name: 'fichierOuLien', formattedSize: '' }]);
                        setInitialFileUrl(resource.fichierOuLien);
                    }
                } catch (err) {
                    if (err.response?.status === 401 || err.response?.status === 403) {
                        await handleApiError(err, dispatch, navigate);
                    } else {
                        setError(t('create_resource.error.fetch_failed'));
                        toast.error(t('create_resource.toasts.fetch_failed'));
                    }
                }
            };
            fetchResource();
        }
    }, [resourceId, isEdit, dispatch, navigate, t]);

    const typeOptions = [
        { value: 'PDF', label: t('create_resource.types.PDF') },
        { value: 'article', label: t('create_resource.types.article') },
        { value: 'vidéo', label: t('create_resource.types.vidéo') },
    ];

    const categoryOptions = [
        { value: 'virtualisation', label: t('create_resource.categories.virtualisation') },
        { value: 'sécurité', label: t('create_resource.categories.sécurité') },
        { value: 'conteneurs', label: t('create_resource.categories.conteneurs') },
        { value: 'cloud', label: t('create_resource.categories.cloud') },
        { value: 'IA', label: t('create_resource.categories.IA') },
    ];

    const tagOptions = [
        { value: 'IT', label: t('create_resource.tags.IT') },
        { value: 'virtualisation', label: t('create_resource.tags.virtualisation') },
        { value: 'infrastructure', label: t('create_resource.tags.infrastructure') },
        { value: 'sécurité', label: t('create_resource.tags.sécurité') },
        { value: 'cybersécurité', label: t('create_resource.tags.cybersécurité') },
        { value: 'réseau', label: t('create_resource.tags.réseau') },
        { value: 'API', label: t('create_resource.tags.API') },
        { value: 'cloud', label: t('create_resource.tags.cloud') },
        { value: 'IA', label: t('create_resource.tags.IA') },
        { value: 'conteneurs', label: t('create_resource.tags.conteneurs') },
    ];

    const handleDescriptionChange = (event, editor) => {
        const data = editor.getData();
        validation.setFieldValue('description', data);
    };

    const handleImageAccepted = (files) => {
        const file = files[0];
        if (file) {
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            });
            setSelectedImage([file]);
            setDeleteOldImage(true);
            validation.setFieldValue('openGraphImage', file);
        }
    };

    const handleFileAccepted = (files) => {
        const file = files[0];
        if (file) {
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
            });
            setSelectedFile([file]);
            setDeleteOldFile(true);
            validation.setFieldValue('fichierOuLien', file);
        }
    };

    const handleUrlChange = (e) => {
        validation.setFieldValue('fichierOuLien', e.target.value);
    };

    const handleTypeChange = (selected) => {
        validation.setFieldValue('type', selected ? selected.value : '');
    };

    const handleCategoryChange = (selected) => {
        validation.setFieldValue('categorie', selected ? selected.value : '');
    };

    const handleTagsChange = (selected) => {
        validation.setFieldValue('tags', selected ? selected.map(opt => opt.value) : []);
    };

    const handleFichierOuLienTypeChange = (type) => {
        validation.setFieldValue('fichierOuLienType', type);
        validation.setFieldValue('fichierOuLien', null);
        setSelectedFile([]);
        setDeleteOldFile(true);
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return t('create_resource.format_bytes.zero');
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = [
            t('create_resource.format_bytes.bytes'),
            t('create_resource.format_bytes.kb'),
            t('create_resource.format_bytes.mb'),
            t('create_resource.format_bytes.gb'),
            t('create_resource.format_bytes.tb'),
            t('create_resource.format_bytes.pb'),
            t('create_resource.format_bytes.eb'),
            t('create_resource.format_bytes.zb'),
            t('create_resource.format_bytes.yb'),
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const handleDelete = () => {
        validation.resetForm();
        setSelectedImage([]);
        setSelectedFile([]);
        setDeleteOldImage(false);
        setDeleteOldFile(false);
        toast.info(t('create_resource.toasts.form_cleared'));
    };

    document.title = t(isEdit ? 'create_resource.title_edit' : 'create_resource.title');

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        title={t(isEdit ? 'create_resource.title_edit' : 'create_resource.title')}
                        pageTitle={t('create_resource.page_title')}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                    {error && <Alert color="danger">{error}</Alert>}
                    <Modal
                        isOpen={isLoading}
                        centered
                        className="loading-modal"
                        backdrop="static"
                    >
                        <ModalHeader className="border-0" />
                        <ModalBody className="text-center">
                            <div className="loading-spinner" />
                            <p className="mt-3">{t(isEdit ? 'create_resource.updating' : 'create_resource.creating')}</p>
                        </ModalBody>
                    </Modal>
                    <Row>
                        <Col lg={8}>
                            <Card>
                                <CardBody>
                                    <div className="mb-3">
                                        <Label className="form-label" htmlFor="resource-title-input">{t('create_resource.form.titre')}</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            id="resource-title-input"
                                            name="titre"
                                            value={validation.values.titre}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={validation.touched.titre && validation.errors.titre}
                                            placeholder={t('create_resource.form.titre_placeholder')}
                                        />
                                        {validation.touched.titre && validation.errors.titre && (
                                            <div className="invalid-feedback">{validation.errors.titre}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <Label className="form-label" htmlFor="resource-thumbnail-img">{t('create_resource.form.openGraphImage')}</Label>
                                        <Dropzone
                                            onDrop={handleImageAccepted}
                                            accept="image/png, image/gif, image/jpeg"
                                            maxFiles={1}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div className="dropzone dz-clickable">
                                                    <div className="dz-message needsclick" {...getRootProps()}>
                                                        <div className="mb-3">
                                                            <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                                        </div>
                                                        <h4>{t('create_resource.form.dropzone_image')}</h4>
                                                    </div>
                                                    <input {...getInputProps()} />
                                                </div>
                                            )}
                                        </Dropzone>
                                        <ul className="list-unstyled mb-0" id="image-preview">
                                            {selectedImage.map((f, i) => (
                                                <Card
                                                    className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                    key={i + '-image'}
                                                >
                                                    <div className="p-2">
                                                        <Row className="align-items-center">
                                                            <Col className="col-auto">
                                                                <img
                                                                    data-dz-thumbnail=""
                                                                    height="80"
                                                                    className="avatar-sm rounded bg-light"
                                                                    alt={f.name}
                                                                    src={f.preview}
                                                                />
                                                            </Col>
                                                            <Col>
                                                                <Link to="#" className="text-muted font-weight-bold">
                                                                    {f.name}
                                                                </Link>
                                                                <p className="mb-0">
                                                                    <strong>{f.formattedSize}</strong>
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </Card>
                                            ))}
                                        </ul>
                                        {validation.touched.openGraphImage && validation.errors.openGraphImage && (
                                            <div className="text-danger">{validation.errors.openGraphImage}</div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <Label className="form-label">{t('create_resource.form.description')}</Label>
                                        <CKEditor
                                            editor={ClassicEditor}
                                            data={validation.values.description}
                                            onChange={handleDescriptionChange}
                                            onBlur={() => validation.setFieldTouched('description', true)}
                                        />
                                        {validation.touched.description && validation.errors.description && (
                                            <div className="text-danger">{validation.errors.description}</div>
                                        )}
                                    </div>

                                    <Row>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="resource-seo-title">{t('create_resource.form.seoTitle')}</Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="resource-seo-title"
                                                    name="seoTitle"
                                                    value={validation.values.seoTitle}
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    invalid={validation.touched.seoTitle && validation.errors.seoTitle}
                                                    placeholder={t('create_resource.form.seoTitle_placeholder')}
                                                />
                                                {validation.touched.seoTitle && validation.errors.seoTitle && (
                                                    <div className="invalid-feedback">{validation.errors.seoTitle}</div>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <Label className="form-label" htmlFor="resource-url-propre">{t('create_resource.form.urlPropre')}</Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="resource-url-propre"
                                                    name="urlPropre"
                                                    value={validation.values.urlPropre}
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    invalid={validation.touched.urlPropre && validation.errors.urlPropre}
                                                    placeholder={t('create_resource.form.urlPropre_placeholder')}
                                                />
                                                {validation.touched.urlPropre && validation.errors.urlPropre && (
                                                    <div className="invalid-feedback">{validation.errors.urlPropre}</div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="mb-3">
                                        <Label className="form-label" htmlFor="resource-meta-description">{t('create_resource.form.metaDescription')}</Label>
                                        <Input
                                            type="textarea"
                                            className="form-control"
                                            id="resource-meta-description"
                                            name="metaDescription"
                                            value={validation.values.metaDescription}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={validation.touched.metaDescription && validation.errors.metaDescription}
                                            placeholder={t('create_resource.form.metaDescription_placeholder')}
                                            rows="4"
                                        />
                                        {validation.touched.metaDescription && validation.errors.metaDescription && (
                                            <div className="invalid-feedback">{validation.errors.metaDescription}</div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">{t('create_resource.form.fichierOuLien_title')}</h5>
                                </CardHeader>
                                <CardBody>
                                    <div>
                                        <p className="text-muted">{t('create_resource.form.fichierOuLien_description')}</p>
                                        <Form>
                                            <FormGroup check className="mb-3">
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="fichierOuLienType"
                                                        value="file"
                                                        checked={validation.values.fichierOuLienType === 'file'}
                                                        onChange={() => handleFichierOuLienTypeChange('file')}
                                                    />
                                                    {t('create_resource.form.upload_file')}
                                                </Label>
                                                <Label check className="ms-3" style={{ paddingLeft: 'inherit' }}>
                                                    <Input
                                                        type="radio"
                                                        name="fichierOuLienType"
                                                        value="url"
                                                        checked={validation.values.fichierOuLienType === 'url'}
                                                        onChange={() => handleFichierOuLienTypeChange('url')}
                                                    />
                                                    {t('create_resource.form.enter_url')}
                                                </Label>
                                            </FormGroup>

                                            {validation.values.fichierOuLienType === 'file' ? (
                                                <Dropzone
                                                    onDrop={handleFileAccepted}
                                                    maxFiles={1}
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <div className="dropzone dz-clickable">
                                                            <div className="dz-message needsclick" {...getRootProps()}>
                                                                <div className="mb-3">
                                                                    <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                                                </div>
                                                                <h4>{t('create_resource.form.dropzone_file')}</h4>
                                                            </div>
                                                            <input {...getInputProps()} />
                                                        </div>
                                                    )}
                                                </Dropzone>
                                            ) : (
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t('create_resource.form.url_placeholder')}
                                                    value={validation.values.fichierOuLien || ''}
                                                    onChange={handleUrlChange}
                                                    onBlur={validation.handleBlur}
                                                    invalid={validation.touched.fichierOuLien && validation.errors.fichierOuLien}
                                                />
                                            )}

                                            {validation.values.fichierOuLienType === 'file' && (
                                                <ul className="list-unstyled mb-0" id="file-preview">
                                                    {selectedFile.map((f, i) => (
                                                        <Card
                                                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                            key={i + '-file'}
                                                        >
                                                            <div className="p-2">
                                                                <Row className="align-items-center">
                                                                    <Col className="col-auto">
                                                                        <img
                                                                            data-dz-thumbnail=""
                                                                            height="80"
                                                                            className="avatar-sm rounded bg-light"
                                                                            alt={f.name}
                                                                            src={f.preview}
                                                                        />
                                                                    </Col>
                                                                    <Col>
                                                                        <Link to="#" className="text-muted font-weight-bold">
                                                                            {f.name}
                                                                        </Link>
                                                                        <p className="mb-0">
                                                                            <strong>{f.formattedSize}</strong>
                                                                        </p>
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </Card>
                                                    ))}
                                                </ul>
                                            )}
                                            {validation.touched.fichierOuLien && validation.errors.fichierOuLien && (
                                                <div className="text-danger">{validation.errors.fichierOuLien}</div>
                                            )}
                                        </Form>
                                    </div>
                                </CardBody>
                            </Card>

                            <div className="text-end mb-4">
                                <button type="button" className="btn btn-danger w-sm me-1" onClick={handleDelete}>
                                    {t('create_resource.form.delete_button')}
                                </button>
                                <button type="button" className="btn btn-success w-sm" onClick={validation.handleSubmit}>
                                    {t(isEdit ? 'create_resource.form.update_button' : 'create_resource.form.create_button')}
                                </button>
                            </div>
                        </Col>

                        <Col lg={4}>
                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">{t('create_resource.form.type_title')}</h5>
                                </CardHeader>
                                <CardBody>
                                    <div>
                                        <Label htmlFor="resource-type-input" className="form-label">{t('create_resource.form.type')}</Label>
                                        <Select
                                            id="resource-type-input"
                                            options={typeOptions}
                                            value={typeOptions.find((opt) => opt.value === validation.values.type)}
                                            onChange={handleTypeChange}
                                            onBlur={() => validation.setFieldTouched('type', true)}
                                            className={validation.touched.type && validation.errors.type ? 'is-invalid' : ''}
                                            placeholder={t('create_resource.form.type_placeholder')}
                                        />
                                        {validation.touched.type && validation.errors.type && (
                                            <div className="invalid-feedback">{validation.errors.type}</div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">{t('create_resource.form.category_tags_title')}</h5>
                                </CardHeader>
                                <CardBody>
                                    <div className="mb-3">
                                        <Label htmlFor="resource-category-input" className="form-label">{t('create_resource.form.categorie')}</Label>
                                        <Select
                                            id="resource-category-input"
                                            options={categoryOptions}
                                            value={categoryOptions.find((opt) => opt.value === validation.values.categorie)}
                                            onChange={handleCategoryChange}
                                            onBlur={() => validation.setFieldTouched('categorie', true)}
                                            className={validation.touched.categorie && validation.errors.categorie ? 'is-invalid' : ''}
                                            placeholder={t('create_resource.form.categorie_placeholder')}
                                        />
                                        {validation.touched.categorie && validation.errors.categorie && (
                                            <div className="invalid-feedback">{validation.errors.categorie}</div>
                                        )}
                                    </div>

                                    <div>
                                        <Label htmlFor="resource-tags-input" className="form-label">{t('create_resource.form.tags')}</Label>
                                        <Select
                                            id="resource-tags-input"
                                            isMulti
                                            options={tagOptions}
                                            value={tagOptions.filter((opt) => validation.values.tags.includes(opt.value))}
                                            onChange={handleTagsChange}
                                            onBlur={() => validation.setFieldTouched('tags', true)}
                                            className={validation.touched.tags && validation.errors.tags ? 'is-invalid' : ''}
                                            placeholder={t('create_resource.form.tags_placeholder')}
                                        />
                                        {validation.touched.tags && validation.errors.tags && (
                                            <div className="invalid-feedback">{validation.errors.tags}</div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ResourceForm;